/**
 * Чипы проверок полосы «Что дальше» окна макета.
 *
 * Шесть проверок отвечают на вопрос «готов ли макет к следующему шагу»:
 * исходник, атрибуты, комплект, файлы печати, визуалы, карточки. У каждой
 * есть тон и клик, который ведёт на вкладку, где причину можно разобрать.
 * Проверка без данных не молчит, а говорит «нет данных» с причиной.
 */

import type { NextStepCheck } from '../../../../components/ui/NextStepBar'
import type { MarketplaceCardDto } from '../../../pipeline/api'
import { techChips } from '../../lib'
import { isBlocked } from './marketplaceCardState'
import { liveArticleCount } from './designPresence'
import type { KitSummary } from './ProductsTab'
import type { ProductVisuals } from './MarketplaceTab'

export type DesignTabKey = 'attrs' | 'printFiles' | 'products' | 'marketplace' | 'history'

/** Поля, которые человек обязан заполнить у макета (необязательные: имя и возраст на макете). */
export const REQUIRED_ATTRIBUTE_FIELDS = [
  'type',
  'category',
  'brand_ru',
  'gender',
  'age',
  'occasion',
  'language',
  'image_subject',
  'image_text',
] as const

export function attributeCompleteness(form: Record<string, string>): { filled: number; total: number } {
  const filled = REQUIRED_ATTRIBUTE_FIELDS.filter((field) => String(form[field] ?? '').trim() !== '').length
  return { filled, total: REQUIRED_ATTRIBUTE_FIELDS.length }
}

/** Итог техконтроля одной строкой — вместо четырёх чипов без расшифровки */
export function techSummary(techCheck: unknown): { text: string; tone: 'ok' | 'warn' | 'none' } {
  if (techCheck == null || typeof techCheck !== 'object') {
    return { text: 'Файлы ещё не проверяли', tone: 'none' }
  }
  const failed = techChips(techCheck).filter((chip) => chip.state === 'fail')
  if (failed.length === 0) return { text: 'Файлы проверены, замечаний нет', tone: 'ok' }
  return { text: `Замечания: ${failed.map((chip) => chip.label).join(', ')}`, tone: 'warn' }
}

export interface DesignChecksInput {
  techCheck: unknown
  form: Record<string, string>
  kit: KitSummary | null
  /** Артикулы товаров макета; null — ещё не загружены. */
  articles: string[] | null
  /** У каких артикулов есть файл печати; null — файлы не загружены. */
  printArticles: Set<string> | null
  visuals: ProductVisuals[] | null
  /** Визуалы на этой стадии вообще бывают. */
  visualsKnown: boolean
  cards: MarketplaceCardDto[] | null
  onGo: (tab: DesignTabKey) => void
}

export function buildDesignChecks(input: DesignChecksInput): NextStepCheck[] {
  const { techCheck, form, kit, articles, printArticles, visuals, visualsKnown, cards, onGo } = input
  const total = articles?.length ?? 0
  const checks: NextStepCheck[] = []

  const tech = techSummary(techCheck)
  checks.push({
    key: 'source',
    label: 'Исходник',
    value: tech.tone === 'ok' ? 'проверен' : tech.tone === 'warn' ? 'замечания' : undefined,
    tone: tech.tone === 'ok' ? 'ok' : tech.tone === 'warn' ? 'warn' : 'none',
    title: tech.tone === 'none' ? 'файлы ещё не проверяли' : tech.text,
  })

  const attrs = attributeCompleteness(form)
  checks.push({
    key: 'attrs',
    label: 'Атрибуты',
    value: `${attrs.filled} из ${attrs.total}`,
    tone: attrs.filled === attrs.total ? 'ok' : 'warn',
    title: 'Заполнено обязательных полей макета',
    onClick: () => onGo('attrs'),
  })

  checks.push(
    kit
      ? {
          key: 'kit',
          label: 'Комплект',
          value: `${kit.created} из ${kit.expected}`,
          tone: kit.isComplete ? 'ok' : 'warn',
          title: 'Создано товаров из положенных по матрице типов',
          onClick: () => onGo('products'),
        }
      : {
          key: 'kit',
          label: 'Комплект',
          tone: 'none',
          title: 'комплектность макета не рассчитана',
        },
  )

  if (articles === null || printArticles === null) {
    checks.push({ key: 'print', label: 'Файлы печати', tone: 'none', title: 'данные ещё загружаются' })
  } else if (total === 0) {
    checks.push({ key: 'print', label: 'Файлы печати', tone: 'none', title: 'у макета нет товаров', onClick: () => onGo('printFiles') })
  } else {
    const ready = articles.filter((article) => printArticles.has(article)).length
    checks.push({
      key: 'print',
      label: 'Файлы печати',
      value: `${ready} из ${total}`,
      tone: ready === total ? 'ok' : 'warn',
      title: 'Товаров, у которых есть файл для печати',
      onClick: () => onGo('printFiles'),
    })
  }

  if (!visualsKnown || visuals === null || articles === null) {
    checks.push({
      key: 'visuals',
      label: 'Визуалы',
      tone: 'none',
      title: 'визуалы появляются на стадии после генерации',
      onClick: () => onGo('marketplace'),
    })
  } else {
    const withVisuals = articles.filter((article) =>
      visuals.some((item) => item.article === article && (item.image_urls?.length ?? 0) > 0),
    ).length
    checks.push({
      key: 'visuals',
      label: 'Визуалы',
      value: `${withVisuals} из ${total}`,
      tone: total > 0 && withVisuals === total ? 'ok' : 'warn',
      title: 'Товаров, у которых есть визуалы',
      onClick: () => onGo('marketplace'),
    })
  }

  if (cards === null || articles === null) {
    checks.push({ key: 'cards', label: 'Карточки', tone: 'none', title: 'данные ещё загружаются' })
  } else if (cards.length === 0) {
    checks.push({
      key: 'cards',
      label: 'Карточки',
      tone: 'none',
      title: 'карточек на площадках ещё нет',
      onClick: () => onGo('marketplace'),
    })
  } else {
    const live = liveArticleCount(cards, articles)
    checks.push({
      key: 'cards',
      label: 'Карточки',
      value: `на витрине ${live} из ${total}`,
      tone: cards.some((card) => isBlocked(card)) ? 'bad' : total > 0 && live === total ? 'ok' : 'warn',
      title: 'Товаров, которые показывает хотя бы одна площадка',
      onClick: () => onGo('marketplace'),
    })
  }

  return checks
}
